// =====================================================
// Master role-aware sidebar — used in all dashboards
//
// R-NAV-2 (spec §3.2.1–3.2.11): full redesign of all 11 role sidebars to
// link to each role's REAL tools (not mock pages). Mock /instructor +
// /authoring are gone; real surfaces (/admin/course-content, /gradebook,
// /catalog, /my-courses, /tutor, …) are in. Not-yet-built destinations
// (⚫ NEW in the spec) point to ComingSoon placeholders (PLACEHOLDER_ROUTES)
// with the correct title — the nav structure is final; content lands in the
// owning sub-R. Items can carry an optional CASL `can` gate (spec §2.7-4);
// items the active ability forbids are filtered out and groups that end up
// empty are hidden.
//
// Phase-14.5 C4: the SIDEBAR_BY_ROLE structure is a flat list of "header"
// items (`{ h }`) interleaved with "link" items (`{ id, t, ic, can? }`).
// =====================================================
import React from "react";
import { Icon } from "./icons";
import { useRole, type RoleId } from "./role";
import { useAbility } from "./auth/Can";
import { useChatUnread } from "./hooks/useChatUnread";
import type { Actions, Subjects, AppAbility } from "./auth/ability";
import type { Go } from "./router";
import { isRetiredRoute } from "./config/adapt-flags";

// R-CHAT M4 — Persian-digit helper for the «پیام‌ها» sidebar unread badge.
const toFaDigitsNav = (n: number): string =>
  String(n).replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

interface SidebarHeader {
  h: string;
}
export interface SidebarLink {
  /** Route key `go(...)` navigates to (1- or 2-segment, e.g. "admin/programs"). */
  id: string;
  t: string;
  ic: string;
  /** Optional CASL gate — item hidden unless ability.can(action, subject). */
  can?: { action: Actions; subject: Subjects };
}
type SidebarEntry = SidebarHeader | SidebarLink;

const isHeader = (e: SidebarEntry): e is SidebarHeader =>
  (e as SidebarHeader).h !== undefined;

// CASL gate reused on every "audit" link (spec §2.6: /audit → read:AuditLog).
const CAN_AUDIT = { action: "read", subject: "AuditLog" } as const;

// Admin academic/people/content/system/governance groups. Extracted so the
// super_admin sidebar can reuse the full set (spec §3.2.5: "همه‌ی گروه‌های
// مدیر سیستم … عیناً مثل ۳.۲.۴") without duplicating it.
const ADMIN_SIDEBAR: SidebarEntry[] = [
  // ساختار آکادمیک — strictly top-down so the hierarchy is self-evident:
  // دانشکده → بخش → گروه آموزشی → رشته → گرایش → چارت درسی. (D-2026-06-14:
  // relabelled per owner — «هیأت‌ها» collided with هیئت علمی and «برنامه‌ها»
  // was unclear; درس/پیش‌نیاز/عمومی-تخصصی are inside چارت درسی, spelled out.)
  { h: "ساختار آکادمیک (از بالا به پایین)" },
  { id: "admin", t: "داشبورد مدیریت", ic: "home" },
  // S-NRI-2 — the ORGANIZATIONAL tree (وزارت نیرو → هلدینگ → شرکت → واحد) is a
  // different axis from the academic one below (دانشکده → گروه → رشته); it sits
  // FIRST because the whole academic structure lives inside one of its nodes.
  // Read-only in this package (CRUD lands with the role matrix in S-NRI-3).
  { id: "admin/organizations", t: "ساختار سازمانی (وزارت تا واحد)", ic: "layers" },
  { id: "admin/schools", t: "دانشکده‌ها", ic: "grad" },
  { id: "admin/faculties", t: "بخش‌های دانشکده", ic: "layers" },
  { id: "admin/departments", t: "گروه‌های آموزشی", ic: "folder" },
  { id: "admin/programs", t: "رشته‌ها", ic: "cert" },
  { id: "admin/specializations", t: "گرایش‌ها", ic: "target" },
  { id: "curriculum-builder", t: "چارت درسی (دروس، پیش‌نیاز، نوع درس)", ic: "book" },
  { id: "faculty", t: "هیئت علمی", ic: "users" },
  { id: "admin/offerings", t: "ارائه دروس و کلاس‌ها", ic: "live" },
  { id: "admin/reference-data", t: "داده‌های مرجع", ic: "layers" },
  { id: "admin/ministry-library", t: "کتابخانه وزارت علوم", ic: "book" },
  { id: "admin/cohorts", t: "دسته‌های قدیمی (Cohort)", ic: "users" },
  { h: "افراد" },
  { id: "admin/people", t: "فهرست افراد", ic: "users" },
  { id: "network", t: "شبکهٔ من", ic: "users" },
  { id: "admin/student-overview", t: "پروندهٔ افراد", ic: "file" },
  { id: "admin/profiles", t: "پروفایل‌ها", ic: "user" },
  { id: "admin/students", t: "دانشجویان", ic: "users" },
  { id: "admin/instructors", t: "اساتید", ic: "grad" },
  { id: "admin/users", t: "مدیریت کاربران", ic: "users" },
  // R-MON W4 — connect a real والد to a دانشجو (runtime onboarding; was seed-only).
  { id: "admin/parent-links", t: "پیوند والد و دانشجو", ic: "link" },
  { id: "admin/roles", t: "نقش‌ها و دسترسی‌ها", ic: "shield" },
  { id: "admin/applications", t: "درخواست‌ها (پذیرش)", ic: "inbox" },
  { id: "admin/hr", t: "استخدام و پروندهٔ پرسنلی", ic: "file" },
  { id: "admin/enrollments", t: "ثبت‌نام‌ها", ic: "cert" },
  { h: "محتوا" },
  { id: "admin/course-content", t: "تولید محتوا", ic: "sparkle" },
  { id: "library", t: "کتابخانه", ic: "folder" },
  { h: "سامانه" },
  { id: "admin/terms", t: "نیم‌سال‌ها", ic: "calendar" },
  { id: "admin/degree-policies", t: "آستانه‌های مقطع", ic: "shield" },
  { id: "admin/regulation", t: "آیین‌نامه آموزشی", ic: "shield" },
  { id: "admin/commission", t: "کمیسیون موارد خاص", ic: "shield" },
  { id: "admin/stage-progress", t: "پیشرفت مرحله‌ای (پزشکی)", ic: "target" },
  { id: "admin/workflows", t: "سازندهٔ گردش‌کار", ic: "inbox" },
  { id: "admin/calendar", t: "تقویم آموزشی", ic: "calendar" },
  // R-COMPLY — مدیریتِ مالی (شهریه/حقوق/خزانه/احکام/حق‌التدریس) کارِ «مدیر مالی» است،
  // نه ریاست دانشگاه. این آیتم‌ها از منوی ادمین برداشته شدند و به منوی finance_manager
  // (و نظارتیِ super_admin) منتقل شدند. «پلن‌ها و قیمت‌ها» (قیمت‌گذاری محصول) با ادمین می‌ماند.
  { id: "admin/plans", t: "پلن‌ها و قیمت‌ها", ic: "dollar" },
  { id: "admin/integrations", t: "یکپارچه‌سازی‌ها", ic: "chip" },
  { id: "admin/notif-templates", t: "قالب‌های اعلان", ic: "bell" },
  { id: "admin/broadcast", t: "پخش اعلان سراسری", ic: "bell" },
  { h: "حاکمیت" },
  { id: "audit", t: "گزارش حسابرسی", ic: "shield", can: CAN_AUDIT },
  { id: "ai-governance", t: "کنسول حاکمیت AI", ic: "sparkle" },
  // R-MON W12/W13 — real system-health console (counts + Node runtime depth).
  { id: "monitoring", t: "سلامت سامانه", ic: "pulse" },
  { id: "analytics-admin", t: "گزارش‌ها و تحلیل‌ها", ic: "chart" },
  { id: "settings", t: "تنظیمات سامانه", ic: "settings" },
];

export const SIDEBAR_BY_ROLE: Record<RoleId, SidebarEntry[]> = {
  // ===== §3.2.1 Student =====
  student: [
    { h: "یادگیری" },
    // homeRoute is "progress" (the live learner page the student lands on after
    // login); the sidebar "میز کار" must point there too so it highlights on
    // landing and doesn't divert to the mock /dashboard.
    { id: "progress", t: "میز کار", ic: "home" },
    { id: "my-courses", t: "دروس من", ic: "book" },
    { id: "catalog", t: "کاتالوگ دروس", ic: "grad" },
    { id: "registration", t: "نامزدسازی سازمانی", ic: "plus" },
    { id: "add-drop", t: "حذف و اضافه", ic: "check" },
    { id: "schedule", t: "برنامهٔ هفتگی", ic: "calendar" },
    { id: "calendar", t: "تقویم", ic: "calendar" },
    { id: "assignments", t: "تکالیف و آزمون‌ها", ic: "check" },
    { id: "transcript", t: "شناسنامهٔ آموزشی کارمند", ic: "file" },
    { id: "my-grades", t: "نمرات و اعتراض", ic: "check" },
    { id: "my-attendance", t: "حضور و غیاب", ic: "shield" },
    { id: "degree-audit", t: "مسیر شایستگی/رتبه", ic: "target" },
    { id: "my-chart", t: "چارت درسی من", ic: "grad" },
    // R-MED — stage-based (پزشکی/دندان/دارو) students; page self-gates for others.
    { id: "my-path", t: "مسیر تحصیلی (مرحله‌محور)", ic: "target" },
    { h: "کلاس و تعامل" },
    { id: "live-class", t: "کلاس‌های زنده", ic: "live" },
    { id: "recordings", t: "آرشیو ضبط‌ها", ic: "video" },
    { id: "community", t: "انجمن گفتگو", ic: "users" },
    { id: "messages", t: "پیام‌ها", ic: "chat" },
    // R-CONNECT — «شبکهٔ من» (ارتباط‌ها/دنبال‌کننده‌ها). Pairs with پیام‌ها; the
    // pendingIncoming badge lives on the global nav chip (shared.tsx Nav).
    { id: "network", t: "شبکهٔ من", ic: "users" },
    { id: "officehours", t: "ساعات حضور مدرس", ic: "headset" },
    { h: "هوش مصنوعی" },
    // S-19 — ONE assistant destination. The old «دستیار هوشمند» (/tutor) was a
    // near-duplicate twin of this; the RAG tutor is now the «پرسش از جزوه‌ها» kind
    // INSIDE /agents, and /tutor redirects here. (owner complaint #6: two places.)
    { id: "agents", t: "دستیارهای هوشمند", ic: "bot" },
    { id: "cognitive-profile", t: "پروفایل شناختی", ic: "chip" },
    { id: "adaptive-path", t: "مسیر یادگیری تطبیقی", ic: "target" },
    { h: "خدمات کارمندی" },
    { id: "service-desk", t: "پیشخوان خدمت", ic: "inbox" },
    // Support ticketing — anyone can open a ticket; the page shows the requester
    // their own tickets + a "new ticket" form, and پشتیبانی reviews them in
    // «صف تیکت‌ها». This entry was missing for end users (owner-reported).
    { id: "tickets", t: "تیکت پشتیبانی", ic: "headset" },
    { id: "wallet", t: "کیف پول", ic: "dollar" },
    { id: "tuition", t: "شهریه / پرداخت", ic: "dollar" },
    // Finance pages that were only reachable via in-page buttons on /tuition —
    // surfaced as direct sidebar items so every built feature is nav-reachable.
    { id: "tuition/loans", t: "وام دانشجویی", ic: "dollar" },
    { id: "tuition/refund", t: "استرداد شهریه", ic: "dollar" },
    { id: "tuition/clearance", t: "تسویه‌حساب", ic: "check" },
    { id: "library", t: "کتابخانه", ic: "folder" },
    { id: "labs", t: "آزمایشگاه مجازی", ic: "flask" },
    { id: "credential", t: "گواهی‌نامه‌ها", ic: "cert" },
    { id: "career", t: "مسیر ارتقای شغلی من", ic: "bolt" },
    { id: "financial-aid", t: "کمک‌هزینه / بورسیه", ic: "dollar" },
    // سلامت و مشاوره — the wellness hub. The validated psychology tests live
    // INSIDE this tab now (the page links to /wellness/tests prominently), so the
    // standalone «تست‌های روان‌شناسی» nav item was removed (owner: keep it in the
    // same tab, not a separate silo).
    { id: "wellness", t: "سلامت و مشاوره", ic: "shield" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "achievements", t: "دستاوردها", ic: "trophy" },
    { id: "portfolio", t: "کارپوشه", ic: "star" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.2 Instructor =====
  instructor: [
    { h: "تدریس" },
    { id: "instructor-dashboard", t: "میز کار", ic: "home" },
    { id: "my-courses", t: "دروس من", ic: "book" },
    { id: "admin/course-content", t: "تولید / ویرایش درس", ic: "sparkle" },
    // R-MARKET P1 — «بازار دانش» authoring studio (course-building skeleton).
    { id: "market/studio", t: "استودیوی دانش (دوره‌سازِ من)", ic: "video" },
    { id: "library", t: "کتابخانهٔ محتوا", ic: "folder" },
    { id: "officehours", t: "ساعات حضور", ic: "headset" },
    { id: "calendar", t: "تقویم و برنامه", ic: "calendar" },
    { h: "ارزشیابی" },
    { id: "gradebook", t: "دفتر نمره", ic: "check" },
    { id: "grading-queue", t: "صف تصحیح تکالیف", ic: "file" },
    { id: "question-bank", t: "بانک سوال", ic: "chip" },
    { id: "assessment-builder", t: "آزمون‌ساز", ic: "sparkle" },
    { id: "rubric-builder", t: "روبریک‌ساز", ic: "target" },
    { id: "grade-submission", t: "ثبت نمرات", ic: "file" },
    { h: "فراگیران" },
    { id: "roster", t: "فهرست فراگیران", ic: "users" },
    { id: "attendance", t: "حضور و غیاب", ic: "check" },
    { id: "class-analytics", t: "تحلیل کلاس", ic: "chart" },
    { id: "item-analysis", t: "تحلیل سوالات", ic: "chart" },
    { h: "کلاس و هوش مصنوعی" },
    { id: "live-class", t: "میزبانی کلاس زنده", ic: "live" },
    { id: "copilot", t: "دستیار هوشمند مدرس", ic: "sparkle" },
    { id: "messages", t: "پیام‌ها", ic: "chat" },
    { id: "network", t: "شبکهٔ من", ic: "users" },
    { id: "tickets", t: "تیکت پشتیبانی", ic: "headset" },
    { h: "مالی" },
    { id: "grade-submission", t: "ثبت نمرات", ic: "file" },
    { id: "payroll/claims", t: "حق‌التدریس", ic: "dollar" },
    { id: "payroll/my-payslips", t: "فیش‌های حقوقی", ic: "dollar" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.3 Faculty admin / ریاست دانشکده (scope-bound to own faculty) =====
  faculty_admin: [
    { h: "دانشکده (از بالا به پایین)" },
    { id: "faculty-dashboard", t: "داشبورد دانشکده", ic: "home" },
    { id: "admin/departments", t: "گروه‌های آموزشی", ic: "folder" },
    { id: "admin/programs", t: "رشته‌ها", ic: "cert" },
    { id: "admin/specializations", t: "گرایش‌ها", ic: "target" },
    { id: "curriculum-builder", t: "چارت درسی (دروس، پیش‌نیاز، نوع درس)", ic: "book" },
    { id: "faculty", t: "هیئت علمی", ic: "users" },
    { h: "آموزش" },
    { id: "admin/courses", t: "کاتالوگ دروس", ic: "book" },
    { id: "admin/offerings", t: "ارائه دروس / کلاس‌بندی", ic: "live" },
    { id: "instructor-assignment", t: "تخصیص استاد", ic: "users" },
    { h: "امور دانشجویی" },
    { id: "admin/student-overview", t: "پروندهٔ افراد", ic: "file" },
    { id: "admin/hr", t: "استخدام دانشکده", ic: "file" },
    { id: "probation-review", t: "بررسی مشروطی", ic: "shield" },
    { id: "admin/commission", t: "کمیسیون موارد خاص", ic: "file" },
    // R-MED — stage-based programs (پزشکی/دندان/دارو): the dean records/reads
    // students' stage progression + reviews the stage structure (read-only CRUD).
    { id: "admin/stage-progress", t: "پیشرفت مرحله‌ای (پزشکی)", ic: "target" },
    { id: "service-desk", t: "پیشخوان (تأییدها)", ic: "inbox" },
    { h: "گزارش‌ها" },
    { id: "faculty-reports", t: "گزارش‌های دانشکده", ic: "chart" },
    { id: "inbox", t: "اعلان‌ها", ic: "bell" },
    { id: "messages", t: "پیام‌ها", ic: "chat" },
    { id: "network", t: "شبکهٔ من", ic: "users" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.4 System admin =====
  admin: ADMIN_SIDEBAR,

  // ===== §3.2.5 Super admin = super tools + all admin groups + global config =====
  // R-MON W12 (honesty): the production deployment is SINGLE-TENANT (one
  // university), so the platform-ops MOCKS «جعل هویت» (impersonate) and «پیکربندی
  // چندمستأجری» (multitenancy) — DemoBadge pages with no backend — are no longer
  // advertised in the nav (the page files stay, just unlinked). «سلامت سامانه»
  // (system-health) is the real backbone-observability console and rides in via
  // ADMIN_SIDEBAR below, so the super_admin-local duplicate is dropped too.
  super_admin: [
    { h: "ابرمدیریت" },
    { id: "super", t: "داشبورد ابرمدیر", ic: "home" },
    { id: "super/tenants", t: "مستأجرها (Tenants)", ic: "grad" },
    ...ADMIN_SIDEBAR,
    // Break-glass finance oversight (the day-to-day finance owner is مدیر مالی).
    { h: "مالی (نظارت)" },
    { id: "admin/finance/treasury", t: "خزانه و گزارش‌ها", ic: "chart" },
    { id: "admin/finance/payroll", t: "اجرای حقوق", ic: "dollar" },
    { id: "admin/finance/decrees", t: "احکام کارگزینی", ic: "dollar" },
    { id: "admin/finance/claims", t: "تأیید حق‌التدریس", ic: "check" },
    { id: "admin/finance/payroll-config", t: "پیکربندی حقوق", ic: "dollar" },
    { id: "admin/finance/withdrawals", t: "صف برداشت", ic: "dollar" },
    { id: "admin/tuition-config", t: "پیکربندی شهریه", ic: "dollar" },
  ],

  // ===== §3.2.6 Parent / guardian =====
  parent: [
    { h: "فرزند" },
    { id: "parent", t: "داشبورد فرزند", ic: "home" },
    { id: "parent/grades", t: "نمرات / کارنامه", ic: "file" },
    { id: "parent/attendance", t: "حضور و غیاب", ic: "check" },
    { id: "calendar", t: "برنامه و تقویم", ic: "calendar" },
    { h: "ارتباط" },
    { id: "inbox", t: "اعلان‌ها", ic: "bell" },
    { id: "messages", t: "پیام به استاد / دانشگاه", ic: "chat" },
    { id: "network", t: "شبکهٔ من", ic: "users" },
    { id: "officehours", t: "جلسه با مشاور", ic: "headset" },
    { id: "tickets", t: "تیکت پشتیبانی", ic: "headset" },
    { h: "مالی" },
    { id: "parent/tuition", t: "وضعیت مالی / شهریه", ic: "dollar" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.7 Teaching assistant (staff) — R-MON W7 =====
  // The TA now has a real section link (SectionAssistant), so its workspace is
  // its ASSIGNED sections only (server 404s elsewhere): roster + class/item
  // analytics (read) + DRAFT grade entry on those sections. FINALIZE (قطعی) stays
  // instructor/admin-only, so «ثبت نمرات» is labelled «ثبت نمرهٔ پیش‌نویس» here and
  // hides the finalize action for a TA.
  ta: [
    { h: "تدریس‌یاری" },
    { id: "ta", t: "میز کار دستیار", ic: "home" },
    { h: "ارزشیابی (تخصیص‌یافته)" },
    { id: "roster", t: "فهرست دانشجویان", ic: "users" },
    { id: "grade-submission", t: "ثبت نمرهٔ پیش‌نویس", ic: "file" },
    { id: "gradebook", t: "دفتر نمره", ic: "check" },
    { id: "class-analytics", t: "تحلیل کلاس", ic: "chart" },
    { id: "item-analysis", t: "تحلیل سؤالات", ic: "chart" },
    { id: "officehours", t: "ساعات حضور", ic: "headset" },
    { id: "calendar", t: "تقویم و برنامه", ic: "calendar" },
    { h: "کلاس" },
    { id: "live-class", t: "کلاس زنده", ic: "live" },
    { id: "messages", t: "پیام‌ها", ic: "chat" },
    { id: "network", t: "شبکهٔ من", ic: "users" },
    { h: "مالی" },
    { id: "payroll/my-payslips", t: "فیش‌های حقوقی", ic: "dollar" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.8 Content manager =====
  content_manager: [
    { h: "محتوا" },
    { id: "content", t: "میز کار محتوا", ic: "home" },
    { id: "admin/course-content", t: "تولید / ویرایش محتوا", ic: "sparkle" },
    // R-MARKET P1 — «بازار دانش» authoring studio (course-building skeleton).
    { id: "market/studio", t: "استودیوی دانش (دوره‌ساز)", ic: "video" },
    // R-MARKET P3 — «بازار دانش» review queue (content_manager = the reviewer §1.2).
    { id: "market/review", t: "بازبینی دوره‌های بازار", ic: "check" },
    { id: "content-review", t: "صف بازبینی محتوا", ic: "check" },
    { id: "my-courses", t: "دروس منتشرشده", ic: "book" },
    { id: "library", t: "کتابخانهٔ محتوا", ic: "folder" },
    { id: "messages", t: "پیام‌ها", ic: "chat" },
    { id: "network", t: "شبکهٔ من", ic: "users" },
    { h: "تحلیل" },
    { id: "content-analytics", t: "تحلیل کاتالوگ", ic: "chart" },
    { h: "مالی" },
    { id: "payroll/my-payslips", t: "فیش‌های حقوقی", ic: "dollar" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== S-NRI-ADAPT — «سازندهٔ پکیج» (استودیو دانش) =====
  // زیرمجموعهٔ سختِ content_manager: فقط پکیج‌های خودش را می‌سازد/منتشر می‌کند. گروهِ
  // تزریق‌شدهٔ «بازار پکیج آموزشی» (buildMarketEntries) کاوش/دوره‌های‌من/علاقه‌مندی را
  // پوشش می‌دهد؛ اینجا فقط میز کار استودیو + دوره‌ساز + حساب. استودیو/payout واقعی = S-NRI-6.
  creator: [
    { h: "استودیو دانش" },
    { id: "creator-studio", t: "میز کار سازنده", ic: "home" },
    { id: "market/studio", t: "دوره‌ساز / پکیج‌ساز", ic: "video" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.9 Support =====
  support: [
    { h: "پشتیبانی" },
    { id: "support", t: "میز کار پشتیبانی", ic: "home" },
    { id: "tickets", t: "صف تیکت‌ها", ic: "inbox" },
    { id: "user-lookup", t: "جست‌وجوی کاربر", ic: "search" },
    { id: "audit", t: "گزارش حسابرسی", ic: "shield", can: CAN_AUDIT },
    { id: "messages", t: "پیام‌ها", ic: "chat" },
    { id: "network", t: "شبکهٔ من", ic: "users" },
    { id: "inbox", t: "اعلان‌ها", ic: "bell" },
    { h: "مالی" },
    { id: "payroll/my-payslips", t: "فیش‌های حقوقی", ic: "dollar" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.10 Moderator =====
  moderator: [
    { h: "نظارت" },
    { id: "moderate", t: "میز کار نظارت", ic: "home" },
    { id: "flagged-reports", t: "صف گزارش‌های پرچم‌شده", ic: "shield" },
    { id: "community-moderation", t: "مدیریت بحث‌ها", ic: "users" },
    { id: "auto-rules", t: "قواعد خودکار", ic: "chip" },
    { id: "moderation-log", t: "تاریخچهٔ اقدامات", ic: "file" },
    { h: "مالی" },
    { id: "payroll/my-payslips", t: "فیش‌های حقوقی", ic: "dollar" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== Finance manager (مدیر مالی) — payroll/treasury/decree workspace =====
  finance_manager: [
    { h: "مالی" },
    // خزانه و گزارش‌ها now also hosts the receivable-side people-360 (R-MON W5):
    // a billing roster (search by name/code + status) → finance-360 detail panel,
    // plus the tuition-receivables report — all on the same Treasury page.
    { id: "admin/finance/treasury", t: "خزانه و گزارش‌ها", ic: "dollar" },
    { id: "admin/finance/payroll", t: "اجرای حقوق", ic: "dollar" },
    { id: "admin/finance/decrees", t: "احکام کارگزینی", ic: "file" },
    { id: "admin/finance/claims", t: "تأیید حق‌التدریس", ic: "check" },
    { id: "admin/finance/payroll-config", t: "پیکربندی حقوق", ic: "settings" },
    { id: "admin/finance/withdrawals", t: "صف برداشت", ic: "dollar" },
    // S-18 — the contracts + salary-assignment section (issue the حکم from a hire).
    { id: "admin/hr", t: "قراردادهای استخدام و حقوق", ic: "file" },
    { h: "شهریه" },
    { id: "admin/tuition-config", t: "پیکربندی شهریه", ic: "dollar" },
    // R-MON W5 — the review queues مدیر مالی OWNS (loan/scholarship/refund
    // approve/reject endpoints are @Roles("finance_manager","super_admin")). These
    // pages role-switch to the REVIEW view for finance_manager; the links were
    // missing so the queues were orphaned.
    { h: "صندوق رفاه و بورسیه" },
    { id: "tuition/loans", t: "وام دانشجویی", ic: "dollar" },
    { id: "financial-aid", t: "بورسیه", ic: "dollar" },
    { id: "tuition/refund", t: "استرداد شهریه", ic: "dollar" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== WELL-4 Counselor (مشاور دانشگاه) — least-privilege wellness desk =====
  // ONLY counselor surfaces: own inbox, appointment queue, the tenant's open
  // wellness crisis queue (duty-of-care), profile, settings. Nothing academic/
  // finance. The crisis queue + a scoped student wellness summary are gated +
  // scoped server-side (own-appointment / open-alert students only).
  counselor: [
    { h: "میز کار مشاور" },
    { id: "counselor-inbox", t: "میز کار مشاور", ic: "home" },
    { id: "counseling-appointments", t: "نوبت‌های مشاوره", ic: "calendar" },
    { id: "crisis-queue", t: "صف موارد بحرانی", ic: "shield" },
    // R-MON W9 — caseload / monitoring: aggregate roll-up + roster + per-student
    // continuity timeline + schedule-follow-up. All scoped to the counselor's own
    // caseload + linked students server-side.
    { id: "counselor-caseload", t: "کاربرگ مراجعان", ic: "users" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== S-NRI-3 org / oversight roles =====
  // READ surfaces only, built from EXISTING routes (no new pages beyond the
  // already-built /admin/organizations). admin/organizations is opened to these
  // roles via a 2-seg route-roles key; the other ids (progress/credential/
  // my-courses/audit) carry no gate → any authed role reaches them.
  // S-NRI-5B.1: «گزارش‌ها و تحلیل» از صفحهٔ mock ‏(pages/Analytics.tsx، اعداد
  // hardcode) به «analytics-admin» منتقل شد — دادهٔ واقعیِ org-rollup، اسکوپ‌شده
  // سمت سرور به زیردرختِ خودِ همین نقش‌ها؛ کلیدِ route-roles همان ۹ نقشِ بک‌اند.
  holding_manager: [
    { h: "هلدینگ" },
    { id: "holding-dashboard", t: "داشبورد", ic: "home" },
    { id: "admin/organizations", t: "ساختار سازمانی", ic: "layers" },
    { id: "analytics-admin", t: "گزارش‌ها و تحلیل", ic: "chart" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],
  company_admin: [
    { h: "شرکت" },
    { id: "company-dashboard", t: "داشبورد", ic: "home" },
    { id: "admin/organizations", t: "ساختار سازمانی", ic: "layers" },
    { id: "analytics-admin", t: "گزارش‌ها و تحلیل", ic: "chart" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],
  company_training_manager: [
    { h: "آموزش شرکت" },
    { id: "training-dashboard", t: "داشبورد", ic: "home" },
    { id: "admin/organizations", t: "ساختار سازمانی", ic: "layers" },
    { id: "analytics-admin", t: "گزارش‌ها و تحلیل", ic: "chart" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],
  certification_officer: [
    { h: "صدور گواهی" },
    { id: "certification-desk", t: "داشبورد", ic: "home" },
    { id: "credential", t: "گواهی‌نامه‌ها", ic: "cert" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],
  practical_assessor: [
    { h: "ارزیابی عملی" },
    { id: "assessor-dashboard", t: "داشبورد", ic: "home" },
    { id: "my-courses", t: "دوره‌های من", ic: "book" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],
  ministry_supervisor: [
    { h: "نظارت وزارت" },
    { id: "ministry-dashboard", t: "داشبورد", ic: "home" },
    { id: "admin/organizations", t: "ساختار سازمانی", ic: "layers" },
    { id: "analytics-admin", t: "گزارش‌ها و تحلیل", ic: "chart" },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],
  auditor: [
    { h: "حسابرسی" },
    { id: "audit-dashboard", t: "داشبورد", ic: "home" },
    { id: "admin/organizations", t: "ساختار سازمانی", ic: "layers" },
    { id: "analytics-admin", t: "گزارش‌ها و تحلیل", ic: "chart" },
    { id: "audit", t: "گزارش حسابرسی", ic: "shield", can: CAN_AUDIT },
    { h: "حساب" },
    { id: "profile", t: "پروفایل", ic: "user" },
    { id: "settings", t: "تنظیمات", ic: "settings" },
  ],

  // ===== §3.2.11 Organisation =====
  org: [
    { h: "سازمان" },
    { id: "org", t: "داشبورد سازمان", ic: "home" },
    { id: "org/users", t: "کاربران سازمان", ic: "users" },
    { id: "org/bulk-enroll", t: "ثبت‌نام گروهی", ic: "plus" },
    { id: "org/seats", t: "صندلی‌ها (Seats)", ic: "check" },
    { id: "org/invoices", t: "فاکتورها", ic: "dollar" },
    { id: "org/reports", t: "گزارش سازمان", ic: "chart" },
    { h: "حساب" },
    { id: "settings", t: "تنظیمات سازمان", ic: "settings" },
  ],
};

// Wrap a list in <ul> if it isn't already (compat with existing CSS).
// Items are real <a href> + onClick so they: show their destination on hover,
// support Cmd/middle-click → background tab, expose aria-current, and are
// keyboard-reachable. href is "/route" (BrowserRouter).
export interface RoleSideNavProps {
  active: string;
  go: Go;
  /**
   * R7.12 — dual-mode rendering for the Mini-variant Drawer.
   * - "expanded" (default): icon + label, group headers visible.
   * - "mini": icon-only, group headers hidden; label via title= + sr-only span.
   */
  mode?: "mini" | "expanded";
}

export interface SidebarGroup {
  h: string;
  items: SidebarLink[];
}

// R-MARKET P4/P5 — «بازار دانش» group, injected into EVERY role's sidebar via
// buildSidebarGroups so it is uniformly discoverable without editing all 13 role
// arrays. The bare /market catalog (PUBLIC) + «دوره‌های من» (buyer shelf, any authed
// role) show for everyone; «درآمد من» (author) + «مدیریت بازار» (admin) are appended
// only for the roles that own them. The authoring «استودیوی دانش» (/market/studio) +
// reviewer «بازبینی» (/market/review) entries stay role-specific in SIDEBAR_BY_ROLE.
const MARKET_AUTHOR_ROLES: ReadonlySet<RoleId> = new Set([
  "instructor",
  "content_manager",
  "admin",
  "super_admin",
]);
const MARKET_ADMIN_ROLES: ReadonlySet<RoleId> = new Set(["admin", "super_admin"]);

const buildMarketEntries = (roleId: RoleId): SidebarEntry[] => {
  const entries: SidebarEntry[] = [
    { h: "بازار پکیج آموزشی" },
    { id: "market", t: "کاوش بازار پکیج", ic: "grad" },
    // Buyer shelf — every authed role may have purchases (doc §6).
    { id: "market/my-courses", t: "دوره‌های من", ic: "book" },
    // R-MARKET EXT — the buyer's wishlist; every authed role may save a course.
    { id: "market/wishlist", t: "علاقه‌مندی‌های من", ic: "heart" },
  ];
  if (MARKET_AUTHOR_ROLES.has(roleId)) {
    entries.push({ id: "market/earnings", t: "درآمد من", ic: "dollar" });
  }
  if (MARKET_ADMIN_ROLES.has(roleId)) {
    entries.push({ id: "market/admin", t: "مدیریت بازار", ic: "settings" });
  }
  return entries;
};

// R-NAV-2/4 — the role's sidebar, CASL-filtered (spec §2.7-4) and grouped,
// with empty groups dropped. Shared by RoleSideNav (the rail/drawer) and the
// /more page (spec §3.5) so both surface exactly the same links.
export const buildSidebarGroups = (
  roleId: RoleId,
  ability: AppAbility,
): SidebarGroup[] => {
  const base = SIDEBAR_BY_ROLE[roleId] || SIDEBAR_BY_ROLE.student;
  // Inject the marketplace group at the top for every role (additive). Buyer shelf
  // is universal; author/admin entries are appended per-role (R-MARKET P5).
  const entries: SidebarEntry[] = [...buildMarketEntries(roleId), ...base].filter(
    (it) =>
      isHeader(it) ||
      // S-NRI-ADAPT — قابلیت‌های دانشگاهیِ بازنشسته (شهریه/وام/چارت مدرک/شبکهٔ اجتماعی
      // …) از ناوبری ناپدید می‌شوند؛ برگشت‌پذیر با برداشتن از RETIRED_MODULES.
      (!isRetiredRoute(it.id) && (!it.can || ability.can(it.can.action, it.can.subject))),
  );
  const groups: SidebarGroup[] = [];
  let current: SidebarGroup | null = null;
  entries.forEach((it) => {
    if (isHeader(it)) {
      current = { h: it.h, items: [] };
      groups.push(current);
    } else if (current) {
      current.items.push(it);
    } else {
      current = { h: "", items: [it] };
      groups.push(current);
    }
  });
  return groups.filter((g) => g.items.length > 0);
};

export const RoleSideNav = ({ active, go, mode = "expanded" }: RoleSideNavProps): React.ReactElement => {
  const { role } = useRole();
  const ability = useAbility();
  // R-CHAT M4 — unread total drives the «پیام‌ها» sidebar badge (same source as
  // the global nav chip; the hook is cheap + shared-shaped so two mounts are fine).
  const { totalUnread: chatUnread } = useChatUnread();
  const isMini = mode === "mini";
  const visibleGroups = buildSidebarGroups(role.id, ability);

  return (
    <aside
      className={"side-nav" + (isMini ? " side-nav-mini" : "")}
      aria-label={`ناوبری نقش ${role.label}`}
      data-mode={mode}
    >
      {visibleGroups.map((g, gi) => (
        <React.Fragment key={gi}>
          {/* Group headers hidden in mini mode; sr-only sibling keeps the
              grouping accessible to screen readers. */}
          {g.h && !isMini ? <h6>{g.h}</h6> : null}
          {g.h && isMini ? <h6 className="sr-only">{g.h}</h6> : null}
          <ul>
            {g.items.map((it) => {
              // R-CHAT M4 — show the unread total as a badge on the «پیام‌ها» item.
              const showChatBadge = it.id === "messages" && chatUnread > 0;
              const badgeText = chatUnread > 99 ? "۹۹+" : toFaDigitsNav(chatUnread);
              return (
                <li key={it.id}>
                  <a
                    href={"/" + it.id}
                    className={active === it.id ? "active" : ""}
                    aria-current={active === it.id ? "page" : undefined}
                    title={isMini ? (showChatBadge ? `${it.t} (${badgeText})` : it.t) : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      go(it.id);
                    }}
                    style={showChatBadge ? { position: "relative" } : undefined}
                  >
                    <Icon name={it.ic} size={14} />
                    {isMini ? <span className="sr-only">{it.t}</span> : it.t}
                    {showChatBadge ? (
                      <span
                        aria-label={`${badgeText} پیام نخوانده`}
                        style={
                          isMini
                            ? {
                                position: "absolute", top: 2, insetInlineEnd: 2,
                                minWidth: 15, height: 15, padding: "0 3px",
                                borderRadius: 8, background: "var(--accent)", color: "#fff",
                                fontSize: 8, fontWeight: 700, lineHeight: "15px", textAlign: "center",
                              }
                            : {
                                marginInlineStart: "auto",
                                minWidth: 18, height: 18, padding: "0 5px",
                                borderRadius: 9, background: "var(--accent)", color: "#fff",
                                fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center",
                              }
                        }
                      >
                        {badgeText}
                      </span>
                    ) : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </React.Fragment>
      ))}
    </aside>
  );
};

export default RoleSideNav;
